import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Compass, Cube, Globe } from "@phosphor-icons/react";

const HERO_BG = "https://images.unsplash.com/photo-1746481594820-04358d2055cd?crop=entropy&cs=srgb&fm=jpg&q=85";
const HERO_VIDEO = "https://videos.pexels.com/video-files/2169880/2169880-hd_1920_1080_30fps.mp4";

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
};

export default function Hero() {
    return (
        <section data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[#040914]">
            {/* Background video with image poster fallback */}
            <div className="absolute inset-0">
                <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster={HERO_BG}
                    data-testid="hero-video"
                    className="w-full h-full object-cover scale-105"
                >
                    <source src={HERO_VIDEO} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-r from-[#040914] via-[#040914]/80 to-[#040914]/20" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#040914]/60 via-transparent to-[#040914]" />
                <div className="absolute inset-0 wms-grid-bg opacity-40" />
            </div>

            {/* Top horizontal info strip */}
            <div className="relative z-10 pt-28 md:pt-32">
                <div className="wms-container">
                    <div className="hidden md:flex items-center justify-between text-[11px] tracking-[0.32em] uppercase text-white/50 border-y border-white/10 py-3">
                        <span>EST · Singapore</span>
                        <span className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-[#48CAE4] rounded-full" /> 24 / 7 Operations Desk
                        </span>
                        <span>IMO Compliant Fleet</span>
                        <span className="text-[#48CAE4]">Charter Lat — 01°16′ N · Lon — 103°50′ E</span>
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className="relative z-10 wms-container mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 pb-24">
                <div className="lg:col-span-8">
                    <motion.div {...fadeUp} transition={{ duration: 0.6 }} className="wms-overline">
                        <span className="inline-flex items-center gap-3">
                            <span className="w-8 h-px bg-[#48CAE4]" />
                            Global Maritime · Chartering · Logistics
                        </span>
                    </motion.div>

                    <motion.h1
                        {...fadeUp}
                        transition={{ duration: 0.7, delay: 0.05 }}
                        data-testid="hero-headline"
                        className="mt-6 font-display font-light text-white text-[clamp(2.4rem,6vw,5rem)] leading-[1.04] tracking-tight"
                    >
                        Global Maritime
                        <br />
                        <span className="text-white/90">Logistics &amp; </span>
                        <span className="italic font-light text-[#48CAE4]">Chartering</span>
                        <br />
                        <span className="text-white/90">Solutions.</span>
                    </motion.h1>

                    <motion.p
                        {...fadeUp}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-white/65"
                    >
                        Delivering reliable vessel chartering, marine support, and cargo
                        transportation services across the world's most demanding trade lanes.
                        Singapore-anchored. Globally operated.
                    </motion.p>

                    <motion.div
                        {...fadeUp}
                        transition={{ duration: 0.7, delay: 0.25 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/contact?intent=charter"
                            data-testid="hero-charter-btn"
                            className="group inline-flex items-center gap-3 px-7 py-4 bg-white text-[#040914] text-xs uppercase tracking-[0.24em] font-medium hover:bg-[#48CAE4] transition-colors"
                        >
                            Request Charter
                            <ArrowUpRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="hero-contact-btn"
                            className="group inline-flex items-center gap-3 px-7 py-4 border border-white/25 text-white text-xs uppercase tracking-[0.24em] font-medium hover:border-[#48CAE4] hover:text-[#48CAE4] transition-colors"
                        >
                            Contact Operations
                        </Link>
                    </motion.div>
                </div>

                {/* Right side stats card stack */}
                <motion.div
                    {...fadeUp}
                    transition={{ duration: 0.8, delay: 0.35 }}
                    className="lg:col-span-4 flex flex-col gap-4"
                >
                    {[
                        { Icon: Globe, label: "Trade Lanes", value: "60+", note: "Active corridors" },
                        { Icon: Cube, label: "Cargo Moved", value: "12.4 Mt", note: "Per annum" },
                        { Icon: Compass, label: "Fleet & Tonnage", value: "38 Vessels", note: "Owned · operated · chartered" },
                    ].map((s, i) => (
                        <div
                            key={s.label}
                            className="wms-glass p-5 flex items-center justify-between group hover:border-[#48CAE4]/40 transition-colors"
                            style={{ animationDelay: `${i * 80}ms` }}
                        >
                            <div className="flex items-center gap-4">
                                <span className="flex items-center justify-center w-12 h-12 bg-[#040914] border border-white/10">
                                    <s.Icon size={22} weight="duotone" className="text-[#48CAE4]" />
                                </span>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.28em] text-white/50">{s.label}</div>
                                    <div className="font-display text-2xl mt-1 text-white">{s.value}</div>
                                </div>
                            </div>
                            <span className="text-[11px] text-white/40 max-w-[110px] text-right leading-snug">
                                {s.note}
                            </span>
                        </div>
                    ))}
                </motion.div>
            </div>

            {/* Bottom marquee */}
            <div className="relative z-10 border-t border-white/10 bg-[#040914]/70 backdrop-blur">
                <div className="overflow-hidden">
                    <div className="wms-marquee-track flex items-center gap-16 py-5 text-white/40 text-[12px] tracking-[0.32em] uppercase whitespace-nowrap">
                        {Array.from({ length: 2 }).map((_, k) => (
                            <span key={k} className="flex items-center gap-16">
                                <span>· Singapore</span><span>· Rotterdam</span><span>· Houston</span>
                                <span>· Shanghai</span><span>· Dubai</span><span>· Santos</span>
                                <span>· Hamburg</span><span>· Mumbai</span><span>· Busan</span>
                                <span>· Lagos</span><span>· Tokyo</span><span>· Antwerp</span>
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
